/* ───────────────────────────────────────────────────────────────────────────
   O16 Mobile price/inventory/SKU sync — owner round-10 (2026-07-17) bug fix.
   NEW, small, non-invasive script — does NOT touch global.js or the base
   variant flow at all.

   ROOT CAUSE: the theme renders the ENTIRE product section TWICE —
   sections/main-product.liquid wraps snippets/main-product-section.liquid
   in BOTH `.main-product-desktop-section-wrapper` AND
   `.main-product-mobile-section-wrapper` (one is display:none per
   breakpoint via CSS, the other visible), whenever the gallery layout
   differs between desktop and mobile. Both copies render the SAME
   `id="price-{{ section.id }}"` (`snippets/main-product-section.liquid`),
   plus `id="Inventory-{{ section.id }}"` and `id="Sku-{{ section.id }}"` —
   genuine DUPLICATE ids in the DOM.

   The base theme's VariantSelects.renderProductInfo() (assets/global.js)
   updates the new variant's price/inventory/SKU via
   `document.getElementById('price-' + sectionId)` etc. — getElementById
   only ever returns the FIRST matching element in document order, which is
   the DESKTOP copy (it renders first in the section's source order — see
   sections/main-product.liquid). On mobile, the desktop wrapper is
   display:none and the (visible) MOBILE copy never gets touched — so the
   shopper sees a stale top price/inventory/SKU after switching variants,
   even though the cart, buy button, and the docked sticky buy-bar price
   (assets/o16-sticky-buybar.js, handled separately and unaffected by this
   bug) all use the correct variant.

   FIX: mirror whichever copy the base theme actually updates (the FIRST in
   document order, matching getElementById) onto every OTHER copy, via a
   MutationObserver on the source only — mirroring can never loop, because
   nothing observes the copies we write to. renderProductInfo() mutates the
   destination element's `.innerHTML` in place (never replaces the element
   node itself), so the source reference captured at init time stays valid
   for the life of the page — no re-querying needed.

   Scoped strictly to `.main-product-desktop-section-wrapper` /
   `.main-product-mobile-section-wrapper` descendants, so product-card and
   recommendation-row price blocks elsewhere on the page are never touched.

   Loaded on the PDP the same way assets/o16-preback-sync.js is
   (sections/main-product.liquid, deferred).
─────────────────────────────────────────────────────────────────────────── */

(function () {
  /* Observers from the previous binding. A page takeover (the bundle ladder
     swaps the whole product section) replaces the very nodes these watch, so
     the old observers end up pointed at detached markup and the mirroring
     silently stops — measured on DEV: after following a set and coming back,
     the desktop copy updated to 299 kr while the MOBILE copy, the one the
     shopper is looking at, stayed on 199 kr. Kept so re-init can disconnect
     them instead of stacking a second set on top. */
  var observers = [];

  /* Mirrors one element group (price / inventory / SKU). No-ops if fewer
     than 2 matching elements exist on the page (e.g. the gallery layout is
     "synchronize" — desktop and mobile share ONE render, so there is no
     duplicate id and nothing to mirror in the first place). */
  function mirrorGroup(selector) {
    var elements = document.querySelectorAll(selector);
    if (elements.length < 2) return;

    // First in DOCUMENT ORDER — exactly what getElementById(id) would
    // return, and exactly what renderProductInfo() actually updates.
    var source = elements[0];
    var targets = Array.prototype.slice.call(elements, 1);

    function mirror() {
      try {
        var html = source.innerHTML;
        targets.forEach(function (target) {
          if (target.innerHTML !== html) {
            target.innerHTML = html;
          }
        });
      } catch (e) {
        /* Never let a mirroring failure break the page — leave the target
           as last rendered rather than throw. */
      }
    }

    // Initial sync in case the two copies were already out of step at load
    // (e.g. a variant was pre-selected via ?variant= in the URL and only
    // one copy's server-rendered markup reflects it).
    mirror();

    var observer = new MutationObserver(mirror);
    observer.observe(source, {
      childList: true,
      subtree: true,
      characterData: true
    });
    observers.push(observer);
  }

  function init() {
    // Re-bindable: every call starts from a clean slate, so waking this after
    // a takeover can never leave two observers writing into the same target.
    observers.forEach(function (o) { o.disconnect(); });
    observers = [];

    // Main price — the primary bug (top price display stuck on the
    // pre-variant-change price on mobile, e.g. 687 kr instead of 959 kr).
    mirrorGroup(
      '.main-product-desktop-section-wrapper .price-wrapper, .main-product-mobile-section-wrapper .price-wrapper'
    );

    // Inventory ("In stock" / "Sold out" / low-stock count) — same
    // duplicate-id staleness bug, same fix. A stale inventory line on
    // mobile would be just as wrong as a stale price.
    mirrorGroup(
      '.main-product-desktop-section-wrapper [id^="Inventory-"], .main-product-mobile-section-wrapper [id^="Inventory-"]'
    );

    // SKU — same pattern.
    mirrorGroup(
      '.main-product-desktop-section-wrapper [id^="Sku-"], .main-product-mobile-section-wrapper [id^="Sku-"]'
    );
  }

  /* The entry a page takeover calls once the new section is in the DOM
     (assets/o16-bundle-ladder.js). Same contract as O16StickyBuybar.init and
     O16PdpTitleFit.refit, for the same reason. */
  window.O16MobilePriceSync = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
